// World address data: countries are bundled; states and cities for a country are
// fetched on demand from /geo/<ISO2>.json (generated by scripts/build-geo.mjs).

import { COUNTRIES } from "./countries";
import { INDIAN_STATES } from "@/components/Invoice/options";

export { COUNTRIES };

const ALIASES = {
  uk: "GB", "u.k.": "GB", "united kingdom": "GB", "great britain": "GB", britain: "GB", england: "GB", scotland: "GB", wales: "GB", "northern ireland": "GB",
  usa: "US", "u.s.a.": "US", "u.s.": "US", us: "US", "united states of america": "US", america: "US",
  uae: "AE", "u.a.e.": "AE", emirates: "AE", "united arab emirates": "AE",
  "south korea": "KR", korea: "KR", "republic of korea": "KR", russia: "RU", vietnam: "VN", "viet nam": "VN",
  "czech republic": "CZ", czechia: "CZ", holland: "NL", "the netherlands": "NL", turkey: "TR", "türkiye": "TR",
  "hong kong": "HK", "hong kong sar": "HK", macau: "MO", taiwan: "TW", iran: "IR", syria: "SY", laos: "LA", bolivia: "BO", venezuela: "VE", tanzania: "TZ", moldova: "MD",
  "ivory coast": "CI", "cote d'ivoire": "CI", "côte d'ivoire": "CI", "saudi": "SA", "ksa": "SA", "bharat": "IN", "hindustan": "IN",
};
const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

export function findCountry(name) {
  const n = norm(name);
  if (!n) return null;
  const iso = ALIASES[n] || (n.length === 2 ? n.toUpperCase() : "");
  return COUNTRIES.find((c) => c[0] === iso || norm(c[1]) === n) || null;
}
export const countryIso = (name) => findCountry(name)?.[0] || "";

// Where the site is served from (works on a sub-path such as GitHub Pages).
export function basePath() {
  if (typeof document === "undefined") return "";
  const s = [...document.scripts].map((x) => x.src).find((src) => src && src.includes("/_next/"));
  return s ? s.slice(0, s.indexOf("/_next/")) : "";
}

const cache = new Map();
export function loadGeo(iso2) {
  if (!iso2) return Promise.resolve({ s: [], c: {} });
  if (!cache.has(iso2)) {
    cache.set(
      iso2,
      fetch(`${basePath()}/geo/${iso2}.json`)
        .then((r) => (r.ok ? r.json() : { s: [], c: {} }))
        .catch(() => {
          cache.delete(iso2);
          return { s: [], c: {} };
        })
    );
  }
  return cache.get(iso2);
}

const sameName = (a, b) => norm(a).replace(/&/g, "and") === norm(b).replace(/&/g, "and");

// Regions to offer for a country. For India the GST state list (with codes) is used.
export function regionsOf(iso2, geo) {
  if (iso2 === "IN") return INDIAN_STATES.map(([code, name]) => ({ code, name }));
  const withCities = (geo?.s || []).filter(([code]) => geo.c?.[code]?.length);
  const list = withCities.length >= 2 || (withCities.length && (geo?.s || []).length > 40) ? withCities : geo?.s || [];
  return list.map(([code, name]) => ({ code, name }));
}

function stateKey(iso2, geo, stateName) {
  if (!stateName) return "";
  const hit = (geo?.s || []).find(([code, name]) => sameName(name, stateName) || code === stateName);
  if (hit) return hit[0];
  if (iso2 === "IN") {
    const gst = INDIAN_STATES.find(([, n]) => sameName(n, stateName));
    const g = gst && norm(gst[1]).replace(/&/g, "and");
    if (gst) return (geo?.s || []).find(([, name]) => norm(name).replace(/&/g, "and").startsWith(g))?.[0] || "";
  }
  return "";
}

// Cities for a region (or the whole country when it's small enough).
export function citiesOf(iso2, geo, stateName) {
  if (!geo?.c) return [];
  const key = stateKey(iso2, geo, stateName);
  if (key) return geo.c[key] || [];
  const all = Object.values(geo.c);
  const count = all.reduce((s, a) => s + a.length, 0);
  return count <= 6000 ? [...new Set(all.flat())].sort((a, b) => a.localeCompare(b)) : [];
}

// Which region a city belongs to.
export function regionOfCity(iso2, geo, city) {
  if (!geo?.c || !city) return "";
  for (const [code, list] of Object.entries(geo.c)) {
    if (list.some((x) => sameName(x, city))) {
      const name = (geo.s || []).find(([c]) => c === code)?.[1] || "";
      if (iso2 === "IN") return INDIAN_STATES.find(([, n]) => sameName(n, name) || sameName(n.replace(/&/g, "and"), name))?.[1] || name;
      return name;
    }
  }
  return "";
}

// Local areas: London boroughs, UK counties. Other countries: free text.
export function areasOf(iso2, geo, city) {
  if (iso2 !== "GB" || !geo?.s) return [];
  const withCities = new Set(Object.keys(geo.c || {}));
  const areas = geo.s.filter(([code]) => !withCities.has(code)).map(([, name]) => name);
  if (/^(greater\s+)?london$/i.test(norm(city))) {
    return areas
      .filter((n) => /^(London Borough of|Royal Borough of (Greenwich|Kensington|Kingston)|City of (London|Westminster))/.test(n))
      .map((n) => n.replace(/^London Borough of /, "").replace(/^Royal Borough of /, ""))
      .sort((a, b) => a.localeCompare(b));
  }
  return areas.filter((n) => !/^London Borough of /.test(n));
}

const POSTCODE = {
  GB: /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i,
  US: /\b(\d{5}(?:-\d{4})?)\b/,
  IN: /\b(\d{3}\s?\d{3})\b/,
  CA: /\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/i,
  AU: /\b(\d{4})\b/,
  DE: /\b(\d{5})\b/,
  FR: /\b(\d{5})\b/,
  AE: null,
};

// Pull country / region / city / postcode out of free-text address lines.
export async function detectPlace({ address = "", country = "", state = "", city = "" } = {}) {
  const text = [address, city, state, country].filter(Boolean).join(", ");
  const out = { country: "", state: "", city: "", postcode: "", address };
  let c = findCountry(country);
  if (!c) {
    const low = ` ${norm(text).replace(/[,.;]/g, " ")} `;
    const names = [...COUNTRIES.map((x) => [x[1], x[0]]), ...Object.entries(ALIASES).filter(([k]) => k.length > 3)].sort((a, b) => b[0].length - a[0].length);
    const hit = names.find(([n]) => low.includes(` ${norm(n)} `));
    if (hit) c = COUNTRIES.find((x) => x[0] === (hit[1].length === 2 ? hit[1] : ALIASES[hit[0]]));
  }
  if (!c) return out;
  out.country = c[1];
  const geo = await loadGeo(c[0]);
  const parts = text.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);
  const regions = regionsOf(c[0], geo);
  out.state = state && regions.find((r) => sameName(r.name, state))?.name || "";
  if (!out.state) {
    for (const p of parts) {
      const r = regions.find((x) => sameName(x.name, p) || sameName(x.name, p.replace(/\s+\S*\d\S*$/, "")) || (c[0] === "US" && new RegExp(`^${x.code}\\b`, "i").test(p.replace(/^.*?,\s*/, ""))) || (c[0] === "US" && new RegExp(`\\b${x.code}\\s+\\d{5}`).test(p)));
      if (r) {
        out.state = r.name;
        break;
      }
    }
  }
  const pc = POSTCODE[c[0]];
  const m = pc && text.match(pc);
  if (m) out.postcode = m[1].toUpperCase();
  const candidates = citiesOf(c[0], geo, out.state);
  const pool = candidates.length ? candidates : Object.values(geo.c || {}).flat();
  let best = "";
  for (const p of parts) {
    const words = p.replace(POSTCODE[c[0]] || /$^/, "").replace(/\b[A-Z]{2}\s*$/, "").trim();
    const found = pool.find((x) => sameName(x, words) || sameName(x, p));
    if (found && found.length > best.length) best = found;
  }
  if (!best && city) best = city;
  out.city = best;
  if (out.city && !out.state) out.state = regionOfCity(c[0], geo, out.city);
  // tidy the street address: drop the parts now held in their own fields
  const drop = [out.country, out.state, out.city, out.postcode, `${out.city} ${out.postcode}`].filter(Boolean).map(norm);
  out.address = parts
    .map((p) => {
      let q = p;
      if (out.postcode) q = q.replace(new RegExp(`\\s*${out.postcode.replace(/\s/g, "\\s*")}\\s*`, "i"), " ").trim();
      if (out.city && sameName(q.replace(/\s+[A-Z]{2}$/, ""), out.city)) return "";
      return q;
    })
    .filter((p) => p && !drop.includes(norm(p)) && !findCountry(p) && !regions.some((r) => sameName(r.name, p)))
    .join(", ");
  return out;
}
